// Web Push helpers — Chrome desktop + Android.
// iOS Safari requires the user to install as PWA + iOS 16.4+; not used here for now.
//
// IMPORTANT: VAPID public key is fetched from the backend at runtime
// (NOT from REACT_APP_VAPID_PUBLIC_KEY) so it works on any deployment as long as
// the backend has VAPID_PUBLIC_KEY/VAPID_PRIVATE_KEY env vars set.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const currentPermission = () =>
  (typeof Notification !== "undefined" && Notification.permission) || "default";

const urlB64ToUint8 = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(safe);
  return new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
};

let cachedVapidKey = null;
const fetchVapidKey = async () => {
  if (cachedVapidKey) return cachedVapidKey;
  try {
    const { data } = await axios.get(`${API}/push/vapid-public-key`);
    cachedVapidKey = (data?.key || "").trim();
    return cachedVapidKey;
  } catch {
    return "";
  }
};

let registrationPromise = null;
const ensureRegistration = () => {
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker.register("/push-sw.js");
  }
  return registrationPromise;
};

/** Ask the OS for notification permission AND register a push subscription on the server.
 *  Returns { ok, reason }. UI is the caller's responsibility. */
export const enablePushNotifications = async () => {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  const vapid = await fetchVapidKey();
  if (!vapid) return { ok: false, reason: "vapid_missing" };
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") return { ok: false, reason: perm };
  const reg = await ensureRegistration();
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  // If a stale subscription exists with a different VAPID key, drop it
  if (sub) {
    try {
      const expected = urlB64ToUint8(vapid);
      const existing = new Uint8Array(sub.options?.applicationServerKey || []);
      const same = existing.length === expected.length && existing.every((b, i) => b === expected[i]);
      if (!same) {
        await sub.unsubscribe();
        sub = null;
      }
    } catch { /* ignore */ }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8(vapid),
    });
  }
  try {
    await axios.post(`${API}/push/subscribe`, {
      subscription: sub.toJSON(),
      user_agent: navigator.userAgent,
    });
  } catch (err) {
    return { ok: false, reason: "server_error" };
  }
  return { ok: true };
};

export const disablePushNotifications = async () => {
  if (!isPushSupported()) return { ok: false };
  const reg = await ensureRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  try { await axios.post(`${API}/push/unsubscribe`, { endpoint }); } catch { /* ignore */ }
  return { ok: true };
};

export const sendTestPush = async () => {
  const { data } = await axios.post(`${API}/push/test`);
  return data;
};
